import {
  Box,
  Button,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  TablePagination,
  CircularProgress,
  Grid,
  Divider,
} from "@mui/material";
import React, { useState, useCallback } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { RightPanel } from "../../../components/RightPanel";
import { useBillingCompanyContext } from "../../../providers";
import { useDispatchAction } from "../../../redux/store.hooks";
import { useFormik } from "formik";
import { Step2ExtractHeaders } from "../../market-campaign/components/create-new-audience/components";
import { validateRowForStatsByMappings } from "../../market-campaign/components/create-new-audience/helpers/validationHelpers";
import { filterValidRows } from "../../market-campaign/components/create-new-audience/helpers/fileGenerator";
import { IssuesDialog } from "../../market-campaign/components/create-new-audience/dialogs/IssuesDialog";
import { Step2SetupMappingLeads } from "./components/Step2SetupMappingLeads";
import { ExcelFileUplaodButtonAsync } from "../../marketingCampaign/components/ExcelFileUplaodButtonAsync";
import { SingleFileDisplayV2 } from "../../../components/FileUpload/SingleFileDisplayV2";
import { PreviewExcelFile } from "../../../components/Preview/PreviewExcelFile";
import { PreviewCSVFile } from "../../../components/Preview/PreviewCSVFile";
import { getFileExtension } from "../../../components/FileUploadIcon/helpers";
import { config } from "../../../config";
import { IHeaderMapping } from "../../market-campaign/components/create-new-audience/CreateAudienceRightPanel.types";
import { bulkUploadLeadsAsync } from "../../../redux/leads/leadsActions";
import { IBulkUploadLeadsPayload } from "../../../redux/leads/leads.types";

export interface IBulkUploadLeadsRightPanelProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  moduleName: string;
  heading?: string;
  tableName?: string;
}

export interface IBulkUploadLeadsForm {
  source_link: string;
  current_step: number;
  headers: string[];
  mappings: IHeaderMapping[];
  isMappingCompleted: boolean;
  sample_data: Record<string, any>[];
  localFile?: File | null;
  status: string;
  actual_status: string;
  drive_status_uuid: string;
  priority: string;
}

const BULK_UPLOAD_STEPS = ["Upload File", "Setup Mapping", "Extract Headers"];
export const MANDATORY_PHONE_FIELDS = ["contact_number_one"];
export const MANDATORY_FIELDS = MANDATORY_PHONE_FIELDS;

export const isPhoneFieldMapped = (
  mappings: {
    selectedMapping?: string;
    isMapped?: boolean;
    isChecked?: boolean;
  }[],
): boolean => {
  return mappings.some((item) => {
    if (!item.selectedMapping) return false;
    if (item.isMapped === false || item.isChecked === false) return false;
    return item.selectedMapping.toLowerCase() === "contact_number_one";
  });
};

const IMAGE_BASE =
  config.assets.s3WhatsappBaseUrl?.replace(/\/$/, "") ||
  config.assets.s3BaseUrl?.replace(/\/$/, "") ||
  "";
const SAMPLE_FILE_URL = `${IMAGE_BASE}/sample/SampleCampaign.xlsx`;
const SAMPLE_CSV_URL = `${IMAGE_BASE}/sample/SampleCampaign.csv`;
const SAMPLE_IMAGE_URL = `${IMAGE_BASE}/sample/SampleImage.png`;

export const BulkUploadLeadsRightPanel: React.FC<
  IBulkUploadLeadsRightPanelProps
> = ({ open, onClose, onSuccess, moduleName, heading, tableName }) => {
  const dispatch = useDispatchAction();
  const companyOutlet = useBillingCompanyContext();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showIssuesDialog, setShowIssuesDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step1Attempted, setStep1Attempted] = useState(false);

  const [validationStats, setValidationStats] = useState({
    total: 0,
    valid: 0,
    issues: 0,
  });

  const submitBulkUpload = async (formValues: IBulkUploadLeadsForm) => {
    setSubmitting(true);
    try {
      const mapping_keys = formValues.mappings
        .filter((mapping) => mapping.selectedMapping)
        .map((mapping) => ({
          check: mapping.isChecked ? 1 : 0,
          label: mapping.selectedMapping,
          column: mapping.header,
        }));

      const hardcoded_values: IBulkUploadLeadsPayload["hardcoded_values"] = {
        priority: formValues.priority || "HOT",
      };
      if (formValues.status) {
        hardcoded_values.status = formValues.status;
      }
      if (formValues.actual_status) {
        hardcoded_values.actual_status = formValues.actual_status;
      }
      if (formValues.drive_status_uuid) {
        hardcoded_values.drive_status_uuid = formValues.drive_status_uuid;
      }

      const payload: IBulkUploadLeadsPayload = {
        source_link: [formValues.source_link],
        mapping_keys,
        hardcoded_values,
        company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
      };

      await dispatch(bulkUploadLeadsAsync(payload));
      onSuccess();
      onClose();
    } catch (error) {
      // Error notification handled by redux thunk
    } finally {
      setSubmitting(false);
    }
  };

  const { values, setValues, setFieldValue } = useFormik<IBulkUploadLeadsForm>({
    initialValues: {
      source_link: "",
      current_step: 0,
      headers: [],
      mappings: [],
      isMappingCompleted: false,
      sample_data: [],
      localFile: null,
      status: "",
      actual_status: "",
      drive_status_uuid: "",
      priority: "",
    },
    onSubmit: async (formValues) => {
      await submitBulkUpload(formValues);
    },
  });

  const isAllMandatoryFieldsMapped = isPhoneFieldMapped(values.mappings);

  const isStatusAndPrioritySelected = Boolean(
    (values.actual_status || values.status || values.drive_status_uuid) &&
    values.priority,
  );

  const statusError =
    step1Attempted &&
    !(values.actual_status || values.status || values.drive_status_uuid)
      ? "Status is required field"
      : undefined;

  const priorityError =
    step1Attempted && !values.priority
      ? "Priority is required field"
      : undefined;

  const handleValidationStatsUpdate = useCallback(
    (stats: { total: number; valid: number; issues: number }) => {
      setValidationStats(stats);
    },
    [],
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSetupMappingClick = () => {
    setStep1Attempted(true);
    const isStatusSelected = Boolean(
      values.actual_status || values.status || values.drive_status_uuid,
    );
    const isPrioritySelected = Boolean(values.priority);

    if (
      !isStatusSelected ||
      !isPrioritySelected ||
      !isAllMandatoryFieldsMapped
    ) {
      return;
    }

    setFieldValue("current_step", 2);
  };

  const handleFileUploadSuccess = (
    path: string,
    extractedHeaders: string[],
    data: Record<string, any>[],
    file?: File,
  ) => {
    setValues((prev) => ({
      ...prev,
      source_link: path,
      headers: extractedHeaders,
      sample_data: data,
      localFile: file || null,
    }));
  };

  const handleProceedWithValidRows = async () => {
    try {
      const validRows = filterValidRows(
        values.sample_data,
        values.headers,
        (row) => validateRowForStatsByMappings(row, values.mappings),
      );

      await setFieldValue("sample_data", validRows);
      setShowIssuesDialog(false);
      await submitBulkUpload(values);
    } catch (error) {
      // Handled in submitBulkUpload
    }
  };

  const handleDialogClose = () => {
    setShowIssuesDialog(false);
  };

  const handleSaveClick = () => {
    if (validationStats.issues > 0) {
      setShowIssuesDialog(true);
      return;
    }
    submitBulkUpload(values);
  };

  return (
    <>
      <RightPanel
        heading={heading || "Bulk Upload Leads"}
        open={open}
        width="100%"
        onClose={onClose}
        actionButtons={() => {
          if (values.current_step === 0) {
            return (
              <Stack direction={"row"} justifyContent={"flex-end"} spacing={2}>
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setFieldValue("current_step", 1)}
                  disabled={!values.source_link}
                >
                  Setup Mapping
                </Button>
              </Stack>
            );
          }
          if (values.current_step === 1) {
            return (
              <Stack direction={"row"} justifyContent={"flex-end"} spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setFieldValue("current_step", 0)}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSetupMappingClick}
                  disabled={!isAllMandatoryFieldsMapped}
                >
                  Extract Headers
                </Button>
              </Stack>
            );
          }
          return (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total rows: {validationStats.total} | Valid rows:{" "}
                  {validationStats.valid} | Rows with issues:{" "}
                  {validationStats.issues}
                </Typography>

                <TablePagination
                  component="div"
                  count={validationStats.total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                  sx={{
                    "& .MuiTablePagination-toolbar": {
                      paddingLeft: 2,
                      paddingRight: 2,
                    },
                  }}
                />
              </Box>

              <Box sx={{ p: 2, borderTop: "1px solid #e0e0e0", width: "100%" }}>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  spacing={2}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setFieldValue("current_step", 1)}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveClick}
                    disabled={
                      !isAllMandatoryFieldsMapped ||
                      !isStatusAndPrioritySelected ||
                      submitting
                    }
                  >
                    {submitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </Stack>
              </Box>
            </>
          );
        }}
      >
        <Box
          sx={{
            width: "100%",
            mb: 4,
          }}
        >
          <Stepper activeStep={values.current_step} alternativeLabel>
            {BULK_UPLOAD_STEPS.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  onClick={() => {
                    if (index === 1 && !values.source_link) return;
                    if (
                      index === 2 &&
                      (!isAllMandatoryFieldsMapped ||
                        !isStatusAndPrioritySelected)
                    )
                      return;
                    setFieldValue("current_step", index);
                  }}
                  sx={{
                    cursor:
                      (index === 1 && !values.source_link) ||
                      (index === 2 &&
                        (!isAllMandatoryFieldsMapped ||
                          !isStatusAndPrioritySelected))
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* STEP 0: Upload Data File */}
        {values.current_step === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Grid container>
                <Grid item xs={12} sx={{ my: 2, ml: -1.5 }}>
                  <Divider sx={{ rotate: "90deg", width: "50px" }} />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Typography
                      sx={{
                        minWidth: "30px",
                        height: "30px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      1
                    </Typography>

                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        Upload Your Data File
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, mb: 2 }}
                      >
                        Upload your CSV or Excel file containing lead details.
                      </Typography>

                      {values.source_link ? (
                        <>
                          <SingleFileDisplayV2
                            file={values.localFile}
                            value={values.source_link}
                            moduleName={moduleName}
                            onDelete={() => {
                              setValues((prev) => ({
                                ...prev,
                                source_link: "",
                                headers: [],
                                mappings: [],
                                sample_data: [],
                                isMappingCompleted: false,
                                localFile: null,
                              }));
                            }}
                          />
                          {values.isMappingCompleted ? (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: "#f0fdf4",
                                borderRadius: 1,
                                border: "1px solid #bbf7d0",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="success.dark"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                ✓ Field mapping completed successfully
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {values.mappings.length} fields mapped
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: "#fef3c7",
                                borderRadius: 1,
                                border: "1px solid #fbbf24",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="warning.dark"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                ⚠️ Field mapping required
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Please complete the field mapping before saving
                              </Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              size="medium"
                              startIcon={<DownloadIcon />}
                              component="a"
                              href={SAMPLE_FILE_URL}
                              download="SampleLeads.xlsx"
                            >
                              Sample XLSX
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="medium"
                              startIcon={<DownloadIcon />}
                              component="a"
                              href={SAMPLE_CSV_URL}
                              download="SampleLeads.csv"
                            >
                              Sample CSV
                            </Button>
                          </Box>

                          <ExcelFileUplaodButtonAsync
                            onUploadSuccess={handleFileUploadSuccess}
                            moduleName={moduleName}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sx={{ my: 2, ml: -1.5 }}>
                  <Divider sx={{ rotate: "90deg", width: "50px" }} />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "warning.main",
                      backgroundColor: "#fff8e1",
                      boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <TipsAndUpdatesIcon
                        sx={{ fontSize: 28, color: "warning.dark" }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{
                          color: "warning.dark",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Important Instructions
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 1,
                        backgroundColor: "error.main",
                        color: "error.contrastText",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PriorityHighIcon sx={{ fontSize: 22 }} />
                      <Typography variant="body2" fontWeight={700}>
                        <Box
                          component="code"
                          sx={{
                            px: 0.75,
                            py: 0.25,
                            mx: 0.5,
                            borderRadius: 0.5,
                            backgroundColor: "rgba(255,255,255,0.25)",
                            fontFamily: "monospace",
                            fontWeight: 700,
                          }}
                        >
                          contact_number_one
                        </Box>
                        column is REQUIRED
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        "& li": { mb: 0.75, fontWeight: 500 },
                        "& li:last-child": { mb: 0 },
                      }}
                    >
                      <li>First row must contain column headers</li>
                      <li>
                        Additional fields like name, email, date of visit can be
                        mapped in step 2
                      </li>
                      <li>Each row represents one lead entry</li>
                      <li>Avoid empty rows in the file</li>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* File Preview Section */}
            {values.source_link ? (
              <Grid item xs={8}>
                {["xlsx", "xls"].includes(
                  getFileExtension(values.source_link),
                ) ? (
                  <PreviewExcelFile filePath={values.source_link} />
                ) : getFileExtension(values.source_link) === "csv" ? (
                  <PreviewCSVFile filePath={values.source_link} />
                ) : (
                  <></>
                )}
              </Grid>
            ) : (
              <Grid item xs={8}>
                <Box
                  sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: "background.default",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Sample layout preview
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    mb={1.5}
                    display="block"
                  >
                    Your file structure should look like this
                  </Typography>
                  <Box
                    component="img"
                    src={SAMPLE_IMAGE_URL}
                    alt="Sample CSV layout"
                    sx={{
                      width: "100%",
                      maxHeight: 420,
                      minHeight: 200,
                      objectFit: "contain",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      display: "block",
                      backgroundColor: "#fff",
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {/* STEP 1: Setup Mapping */}
        {values.current_step === 1 && (
          <Step2SetupMappingLeads
            headers={values.headers}
            data={values.sample_data}
            mappings={values.mappings}
            tableName={tableName}
            onMappingChange={(newMappings) =>
              setFieldValue("mappings", newMappings)
            }
            statusValues={{
              status: values.status,
              actual_status: values.actual_status,
              drive_status_uuid: values.drive_status_uuid,
              priority: values.priority,
            }}
            onStatusChange={(statusData) => {
              setFieldValue("status", statusData.status);
              setFieldValue("actual_status", statusData.actual_status);
              setFieldValue("drive_status_uuid", statusData.drive_status_uuid);
              if (statusData.priority !== undefined) {
                setFieldValue("priority", statusData.priority);
              }
            }}
            statusError={statusError}
            priorityError={priorityError}
          />
        )}

        {/* STEP 2: Extract Headers */}
        {values.current_step === 2 && (
          <Step2ExtractHeaders
            headers={values.headers}
            data={values.sample_data}
            mappings={values.mappings}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onValidationStatsUpdate={handleValidationStatsUpdate}
          />
        )}
      </RightPanel>

      <IssuesDialog
        open={showIssuesDialog}
        onClose={handleDialogClose}
        onProceed={handleProceedWithValidRows}
        issuesCount={validationStats.issues}
        validCount={validationStats.valid}
      />
    </>
  );
};
